/**
 * Die Gastkartei — alles, was sich über einen Gast RECHNEN lässt.
 *
 * WARUM HIER NICHTS GESPEICHERT WIRD: Aufenthalte, Nächte, Umsatz, erster und
 * letzter Besuch, Stornos und No-Shows stehen bereits in `bookings`, `invoices` und
 * `payments`. Eine zweite, gespeicherte Fassung derselben Zahlen wäre eine Fassung,
 * die auseinanderläuft — es genügt eine Gutschrift, ein verschobener Aufenthalt oder
 * eine nachträglich erfasste Zahlung, und der Umsatz in der Kartei stimmt nicht mehr
 * mit dem in der Auswertung überein. Wer dann welchem Wert glaubt, entscheidet der
 * Zufall. Also: immer rechnen, nie führen.
 *
 * Reine Funktionen, kein React, kein Datenzugriff — getestet in guests.test.ts.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ART. 9 DSGVO: `allergies` und `dietary` sind besondere Kategorien personen-
 * bezogener Daten (Gesundheit, mittelbar auch Religion). Sie werden hier nie
 * beiläufig mitgeführt: `buildGuestExport` weist sie gesondert aus, und in der
 * Oberfläche steht, auf welcher Grundlage sie erhoben wurden. Siehe Migration
 * 0043 für die ausführliche Begründung und die CHECK-Bedingung, die eine Angabe
 * ohne Rechtsgrundlage gar nicht erst zulässt.
 * ═══════════════════════════════════════════════════════════════════════════
 */

import type {
  Booking, Guest, Invoice, ISODate, Payment,
} from '../data/model'
import { ageOn, nights } from './dates'
import { fromCents, toCents } from './money'

/* ────────────────────────────────────────────────────────── Datenform */

export type Salutation = 'herr' | 'frau' | 'divers' | 'familie' | 'firma'
export type IdDocumentType = 'passport' | 'id_card' | 'driving_licence' | 'other'
export type FloorPreference = 'ground' | 'upper' | 'any'
export type SmokingPreference = 'non_smoker' | 'smoker'
/**
 * Rechtsgrundlage für die Gesundheitsangaben.
 * `consent`  — ausdrückliche Einwilligung des Gastes (Art. 9 Abs. 2 lit. a).
 * `contract` — zur Erbringung der gebuchten Leistung erforderlich und vom Gast
 *              selbst mitgeteilt.
 */
export type HealthBasis = 'consent' | 'contract'

/**
 * Die Kartei-Felder aus Migration 0043. Als eigenes Interface und nicht in
 * `model.ts`, weil dort gerade andere Hände arbeiten — beim Lesen aus
 * `store.db.guests` wird gecastet, geschrieben wird über `store.saveGuest`.
 */
export interface GuestProfile extends Guest {
  salutation?: Salutation | null
  title?: string | null
  nationality?: string | null
  vat_id?: string | null
  birthdate?: ISODate | null
  id_document?: string | null
  id_document_type?: IdDocumentType | null

  pref_floor?: FloorPreference | null
  pref_smoking?: SmokingPreference | null
  pref_location?: string | null
  pref_bedding?: string | null
  pref_arrival_time?: string | null
  preferences_note?: string | null

  /** BESONDERE KATEGORIE — Art. 9 DSGVO. Siehe Kopf dieser Datei. */
  allergies?: string | null
  /** BESONDERE KATEGORIE — Art. 9 DSGVO. Siehe Kopf dieser Datei. */
  dietary?: string | null
  health_basis?: HealthBasis | null
  health_recorded_at?: string | null

  occasion?: string | null
  occasion_date?: ISODate | null
  vehicle_plate?: string | null
  vehicle_note?: string | null
  pets?: string | null
  source?: string | null

  marketing_consent?: boolean
  marketing_consent_at?: string | null
  marketing_consent_source?: string | null

  tags?: string[]
  vip_override?: boolean | null
  merged_into?: string | null
  anonymized_at?: string | null
  anonymized_by?: string | null
}

/** Was der Betrieb selbst festlegt. Aus `tenants`, siehe Migration 0043. */
export interface GuestPolicy {
  /**
   * Ab diesem gebuchten Gesamtwert gilt ein Gast als VIP. Null = zählt nicht.
   * Gemessen an `GuestStats.booked` und nicht am abgerechneten Umsatz — sonst wäre
   * die Schwelle in jedem Betrieb ohne eigene Rechnungen unerreichbar und der
   * Schalter eine Einstellung, die nichts tut.
   */
  vipRevenueThreshold?: number | null
  /** Ab so vielen Aufenthalten gilt ein Gast als VIP. Null = zählt nicht. */
  vipStayThreshold?: number | null
  /** Belegaufbewahrung in Jahren ab Ende des Kalenderjahres. */
  retentionYears: number
}

export const DEFAULT_POLICY: GuestPolicy = {
  vipRevenueThreshold: null,
  vipStayThreshold: null,
  retentionYears: 7,
}

/* ────────────────────────────────────────────────────────── Kennzahlen */

export interface GuestStats {
  /** Angetretene Aufenthalte: bestätigt, eingecheckt oder abgereist, Anreise erreicht. */
  stays: number
  /** Bestätigte Aufenthalte, die noch bevorstehen. Zählen nicht als Aufenthalt. */
  upcoming: number
  nights: number
  /** Umsatz brutto aus AUSGESTELLTEN Rechnungen; Gutschriften ziehen sich ab. */
  revenue: number
  /**
   * Was dieser Gast GEBUCHT hat: die Bruttosummen seiner angetretenen und
   * bevorstehenden Aufenthalte, aus den Buchungen selbst.
   *
   * ═══════════════════════════════════════════════════════════════════════
   * WARUM ES DIESE ZAHL NEBEN `revenue` BRAUCHT.
   *
   * `revenue` kommt aus ausgestellten Rechnungen. Das ist die richtige Zahl für
   * die Buchhaltung und die falsche für die Kartei: ein Vermieter mit sechs
   * Einheiten schreibt oft gar keine Rechnung — der Gast zahlt bar oder überweist,
   * das Portal rechnet ohnehin selbst ab. In so einem Betrieb steht bei JEDEM
   * Gast dauerhaft „€ 0,00", und die Kartei sieht kaputt aus, obwohl sie richtig
   * rechnet. Eine Zahl, der niemand glaubt, ist schlimmer als keine.
   *
   * Die beiden bleiben getrennt und werden getrennt beschriftet — „gebucht" ist
   * nicht „abgerechnet", und wer sie zu einer Zahl verschmilzt, hat am Ende einen
   * Umsatz, der in der Kartei anders aussieht als in der Auswertung.
   *
   * GEZÄHLT WIRD, WAS AUCH ALS AUFENTHALT ZÄHLT: bestätigt, eingecheckt,
   * abgereist. Nicht `BLOCKING_STATUSES`, obwohl eine Option die Einheit ebenfalls
   * sperrt — eine Option ist eine Reservierung auf Widerruf, und ein Wert, der
   * beim Ablaufen der Frist wieder verschwindet, sieht wie ein Fehler aus. Ausserdem
   * wäre `booked` dann die einzige Zahl hier, die Optionen mitzählt, während
   * `stays` und `upcoming` es nicht tun.
   * ═══════════════════════════════════════════════════════════════════════
   */
  booked: number
  /** Tatsächlich eingegangene Zahlungen, Rückzahlungen abgezogen. */
  paid: number
  /** Was aus ausgestellten Rechnungen noch aussteht. Nie negativ. */
  open: number
  firstStay: ISODate | null
  lastStay: ISODate | null
  /**
   * Die jüngste ANREISE, nicht die jüngste Abreise. Beides wird gebraucht und beides
   * meint etwas anderes: „zuletzt da" ist die Abreise, „unsere Woche im Juli" ist die
   * Anreise — und der Jahrestag, auf den eine Wiederkehr-Ansprache zielt, hängt an
   * der Anreise.
   */
  lastArrival: ISODate | null
  cancellations: number
  noShows: number
  /**
   * Datum des jüngsten Belegs — der Anker für die Aufbewahrungsfrist. Ohne
   * Rechnung fällt er auf die letzte Abreise zurück: auch ohne Beleg soll ein
   * Gast irgendwann zur Löschung vorgeschlagen werden.
   */
  lastDocument: ISODate | null

  /* ── Durchschnitte ───────────────────────────────────────────────────────
     Alles ab hier ist abgeleitet und steht nur deshalb im selben Objekt, weil es
     dieselben Buchungen zweimal durchzugehen nicht wert wäre. Ohne angetretenen
     Aufenthalt sind sie 0 bzw. null — nicht „unendlich" und nicht NaN, denn die
     Oberfläche zeigt sie sonst an, statt sie wegzulassen. */

  /** Ø Umsatz je angetretenem Aufenthalt — abgerechnet, siehe `revenue`. */
  revenuePerStay: number
  /**
   * Ø gebuchter Wert je angetretenem Aufenthalt. Trennt den Gast, der viermal eine
   * Nacht bleibt, von dem, der viermal eine Woche bleibt — und tut das auch dann,
   * wenn nie eine Rechnung geschrieben wurde. Siehe `booked`.
   */
  bookedPerStay: number
  /** Ø Aufenthaltsdauer in Nächten, eine Nachkommastelle. */
  nightsPerStay: number
  /** Die am häufigsten gebuchte Einheit; bei Gleichstand die zuletzt bewohnte. */
  favouriteUnitId: string | null
  /** Wie oft — erst ab zwei Aufenthalten in derselben Einheit ist es eine Vorliebe. */
  favouriteUnitStays: number
  /**
   * MEDIAN der Tage zwischen Buchungseingang und Anreise, nicht der Mittelwert.
   *
   * Zwei Gründe. Erstens die eine Buchung, die zwei Jahre im Voraus kam und einen
   * Mittelwert allein trägt. Zweitens `created_at`: bei einer Portalbuchung ist das
   * der Zeitpunkt des Imports und nicht der der Buchung, und bei einer nachträglich
   * erfassten Buchung liegt es hinter der Anreise. Der Median steckt beides weg,
   * der Mittelwert nicht. Negative Werte werden auf 0 gezogen — wer am Anreisetag
   * bucht, hat null Tage Vorlauf und nicht minus drei.
   */
  leadTimeDays: number | null
}

export const EMPTY_STATS: GuestStats = {
  stays: 0, upcoming: 0, nights: 0, revenue: 0, booked: 0, paid: 0, open: 0,
  firstStay: null, lastStay: null, lastArrival: null,
  cancellations: 0, noShows: 0, lastDocument: null,
  revenuePerStay: 0, bookedPerStay: 0, nightsPerStay: 0,
  favouriteUnitId: null, favouriteUnitStays: 0, leadTimeDays: null,
}

export interface StatsInput {
  guestId: string
  bookings: Booking[]
  invoices: Invoice[]
  payments: Payment[]
  today: ISODate
}

/**
 * Eine Buchung zählt als Aufenthalt, wenn sie stattgefunden hat oder gerade
 * stattfindet. Anfragen und Angebote nicht — der Gast hat nur gefragt. Blockierungen
 * (`kind === 'block'`) sind Wartungssperren und haben ohnehin keinen Gast.
 */
const STAY_STATUSES = new Set(['confirmed', 'checked_in', 'checked_out'])

export function guestStats(input: StatsInput): GuestStats {
  const { guestId, today } = input
  const bookings = input.bookings.filter(
    (b) => b.guest_id === guestId && b.kind !== 'block')
  const bookingIds = new Set(bookings.map((b) => b.id))

  // Eine Rechnung gehört zum Gast, wenn sie auf ihn ausgestellt ist ODER an einer
  // seiner Buchungen hängt. Beides, weil `invoices.guest_id` bei älteren Belegen
  // fehlen kann und weil eine Buchung nachträglich umgehängt worden sein kann.
  const invoices = input.invoices.filter(
    (i) => i.guest_id === guestId || (i.booking_id !== null && bookingIds.has(i.booking_id)))
  const issued = invoices.filter((i) => Boolean(i.issued_at) && i.type !== 'proforma')
  const issuedIds = new Set(issued.map((i) => i.id))

  const payments = input.payments.filter(
    (p) => (p.booking_id !== null && bookingIds.has(p.booking_id))
      || (p.invoice_id !== null && issuedIds.has(p.invoice_id)))

  const stays = bookings.filter(
    (b) => STAY_STATUSES.has(b.status) && b.arrival <= today)
  const upcoming = bookings.filter(
    (b) => STAY_STATUSES.has(b.status) && b.arrival > today)

  // In Cent, damit sich ein Dutzend Rechnungen ohne Rundungsdrift addieren.
  const revenueCents = issued.reduce((a, i) => a + toCents(i.total_gross), 0)
  const paidCents = payments.reduce((a, p) => a + toCents(p.amount), 0)
  // Siehe `GuestStats.booked`: dieselbe Auswahl wie `stays` und `upcoming`, damit
  // die Zahl zu den Aufenthalten passt, die daneben stehen.
  const stayCents = stays.reduce((a, b) => a + toCents(b.total_gross), 0)
  const bookedCents = stayCents
    + upcoming.reduce((a, b) => a + toCents(b.total_gross), 0)

  // Offen kommt aus den Rechnungen selbst, nicht aus Umsatz minus Zahlung: eine
  // Kaution ist eine Zahlung, gehört aber zu keiner Rechnung, und eine stornierte
  // Rechnung ist bezahlt oder nicht — offen ist sie nie.
  const openCents = issued
    .filter((i) => i.status !== 'paid' && i.status !== 'cancelled')
    .reduce((a, i) => a + Math.max(0, toCents(i.total_gross) - toCents(i.paid_amount)), 0)

  const arrivals = stays.map((b) => b.arrival).sort()
  const departures = stays.map((b) => b.departure).sort()
  const invoiceDates = issued.map((i) => i.issue_date).sort()

  const nightsTotal = stays.reduce((a, b) => a + Math.max(0, nights(b.arrival, b.departure)), 0)

  return {
    stays: stays.length,
    upcoming: upcoming.length,
    nights: nightsTotal,
    revenue: fromCents(revenueCents),
    booked: fromCents(bookedCents),
    paid: fromCents(paidCents),
    open: fromCents(openCents),
    firstStay: arrivals[0] ?? null,
    lastStay: departures[departures.length - 1] ?? null,
    lastArrival: arrivals[arrivals.length - 1] ?? null,
    cancellations: bookings.filter((b) => b.status === 'cancelled').length,
    noShows: bookings.filter((b) => b.status === 'no_show').length,
    lastDocument: invoiceDates[invoiceDates.length - 1]
      ?? departures[departures.length - 1] ?? null,

    // In Cent teilen und erst danach zurückrechnen, damit aus 3 × 233,33 € nicht
    // 233,3333333 € werden.
    revenuePerStay: stays.length > 0
      ? fromCents(Math.round(revenueCents / stays.length))
      : 0,
    // Aus `stayCents` und nicht aus `bookedCents`: „Ø je Aufenthalt" beantwortet,
    // was ein Besuch dieses Gastes wert WAR. Der Aufenthalt, der erst nächsten
    // Sommer stattfindet, gehört in die Summe, aber weder in Zähler noch Nenner
    // des Durchschnitts — sonst verdoppelt eine einzige Vorausbuchung den Wert
    // eines Gastes, der bisher genau einmal da war.
    bookedPerStay: stays.length > 0
      ? fromCents(Math.round(stayCents / stays.length))
      : 0,
    nightsPerStay: stays.length > 0
      ? Math.round((nightsTotal / stays.length) * 10) / 10
      : 0,
    ...favouriteUnit(stays),
    // Bevorstehende Aufenthalte zählen mit: eine bestätigte Buchung für nächsten
    // Sommer sagt über die Vorlaufzeit dieses Gastes genauso viel aus wie eine
    // vergangene, und bei einem Gast mit einem einzigen Besuch ist sie die Hälfte
    // der Datenlage.
    leadTimeDays: medianLeadTime([...stays, ...upcoming]),
  }
}

/**
 * Die Einheit, in der dieser Gast am häufigsten war.
 *
 * Bei Gleichstand gewinnt die zuletzt bewohnte und nicht die alphabetisch erste:
 * wer zweimal in A und zweimal in B war, zuletzt in B, will beim nächsten Mal
 * vermutlich B. Eine willkürliche Entscheidung wäre hier schlechter als gar keine —
 * deshalb steht `favouriteUnitStays` daneben, und die Oberfläche zeigt die Kachel
 * erst, wenn die Zahl eine Vorliebe belegt statt sie zu behaupten.
 */
function favouriteUnit(stays: Booking[]): { favouriteUnitId: string | null; favouriteUnitStays: number } {
  if (stays.length === 0) return { favouriteUnitId: null, favouriteUnitStays: 0 }
  const byRecency = [...stays].sort((a, b) => b.arrival.localeCompare(a.arrival))
  const count = new Map<string, number>()
  for (const b of byRecency) count.set(b.unit_id, (count.get(b.unit_id) ?? 0) + 1)

  let bestId: string | null = null
  let best = 0
  // Über die nach Aktualität sortierte Liste laufen und nur bei ECHT mehr wechseln:
  // damit gewinnt bei Gleichstand die zuerst gesehene, also die jüngste.
  for (const b of byRecency) {
    const n = count.get(b.unit_id) ?? 0
    if (n > best) { best = n; bestId = b.unit_id }
  }
  return { favouriteUnitId: bestId, favouriteUnitStays: best }
}

/** Siehe `GuestStats.leadTimeDays` für die Begründung des Medians. */
function medianLeadTime(bookings: Booking[]): number | null {
  const days = bookings
    .filter((b) => typeof b.created_at === 'string' && b.created_at.length >= 10)
    .map((b) => Math.max(0, nights(b.created_at.slice(0, 10), b.arrival)))
    .filter((d) => Number.isFinite(d))
    .sort((a, b) => a - b)
  if (days.length === 0) return null
  const mid = Math.floor(days.length / 2)
  return days.length % 2 === 1
    ? days[mid]!
    : Math.round((days[mid - 1]! + days[mid]!) / 2)
}

/* ────────────────────────────────────────────────────────── Geburtstag */

export interface BirthdayInfo {
  /** Alter in vollen Jahren am Stichtag. */
  age: number
  /** Der nächste Geburtstag ab dem Stichtag; der Stichtag selbst zählt dazu. */
  next: ISODate
  /** Tage bis dahin. 0 = heute. */
  inDays: number
  /** Welchen Geburtstag der Gast dann feiert. */
  turning: number
}

/**
 * Alter und nächster Geburtstag.
 *
 * DER 29. FEBRUAR ist der Fall, an dem so etwas normalerweise scheitert. In einem
 * Nicht-Schaltjahr gibt es den Tag nicht, und `new Date(2027, 1, 29)` ist der 1. März
 * — also gerade nicht der Tag, den der Gast feiert. Wir ziehen stattdessen auf den
 * 28. Februar vor. Das ist eine ENTSCHEIDUNG und keine Rechtsregel: bürgerlich-
 * rechtlich (§ 188 Abs. 3 BGB) endet eine Jahresfrist am letzten Tag des Monats, für
 * einen Glückwunsch zählt aber nur, dass er lieber einen Tag zu früh kommt als einen
 * zu spät.
 *
 * Ein Geburtsdatum in der Zukunft ist ein Tippfehler und kein Gast — dann lieber
 * nichts anzeigen als ein negatives Alter.
 */
export function birthdayInfo(
  birthdate: string | null | undefined, today: ISODate,
): BirthdayInfo | null {
  if (!birthdate || !/^\d{4}-\d{2}-\d{2}$/.test(birthdate)) return null
  if (birthdate > today) return null

  const age = ageOn(birthdate, today)
  if (age < 0 || age > 130) return null

  const month = birthdate.slice(5, 7)
  const day = birthdate.slice(8, 10)
  const thisYear = anniversaryIn(Number(today.slice(0, 4)), month, day)
  const next = thisYear >= today
    ? thisYear
    : anniversaryIn(Number(today.slice(0, 4)) + 1, month, day)

  return {
    age,
    next,
    inDays: Math.max(0, nights(today, next)),
    // Nicht `age + 1`: wer heute Geburtstag hat, wird heute `age`, nicht `age + 1`.
    turning: Number(next.slice(0, 4)) - Number(birthdate.slice(0, 4)),
  }
}

function anniversaryIn(year: number, month: string, day: string): ISODate {
  if (month === '02' && day === '29' && !isLeapYear(year)) return `${year}-02-28`
  return `${year}-${month}-${day}`
}

function isLeapYear(y: number): boolean {
  return (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0
}

export interface BirthdayEntry {
  guest: GuestProfile
  info: BirthdayInfo
}

/**
 * Wer in den nächsten `withinDays` Tagen Geburtstag hat, der nächste zuerst.
 *
 * Anonymisierte und zusammengeführte Datensätze fallen heraus: der eine hat kein
 * Geburtsdatum mehr, der andere ist nur noch der Anker einer alten Rechnung. Ein
 * gesperrter Gast fällt NICHT heraus — der Sperrvermerk verhindert den Versand, nicht
 * die Anzeige, und der Vermieter soll selbst sehen, wen er da vor sich hat.
 */
export function upcomingBirthdays(
  guests: GuestProfile[], today: ISODate, withinDays = 30,
): BirthdayEntry[] {
  const out: BirthdayEntry[] = []
  for (const guest of guests) {
    if (guest.anonymized_at || guest.merged_into) continue
    const info = birthdayInfo(guest.birthdate, today)
    if (!info || info.inDays > withinDays) continue
    out.push({ guest, info })
  }
  return out.sort((a, b) => a.info.inDays - b.info.inDays
    || guestLabel(a.guest).localeCompare(guestLabel(b.guest)))
}

/* ────────────────────────────────────────────── Wiederkehr-Jahrestag */

export interface ReturnEntry {
  guest: GuestProfile
  /** Die Anreise, deren Jahrestag ansteht. */
  lastArrival: ISODate
  /** Der Jahrestag selbst. */
  anniversary: ISODate
  /** Tage bis dahin. */
  inDays: number
  /** Wie viele Jahre seither vergangen sein werden. */
  yearsAgo: number
}

/**
 * „Sie waren letztes Jahr um diese Zeit bei uns."
 *
 * WARUM NICHT AM JAHRESTAG SELBST: wer im Juli in den Bergen war, entscheidet im
 * März, wo er diesen Sommer hinfährt. Eine Mail am 20. Juli erreicht ihn, wenn er
 * längst woanders gebucht hat. Deshalb ein Fenster VOR dem Jahrestag — Vorgabe 3 bis
 * 13 Wochen — und nicht der Tag.
 *
 * Gerechnet wird auf die letzte ANREISE und nicht auf die Abreise: die Anreise ist
 * das Datum, das der Gast im Kopf hat, wenn er an „unsere Woche im Juli" denkt.
 */
export function returnCandidates(
  guests: GuestProfile[],
  statsOf: (id: string) => GuestStats,
  today: ISODate,
  { minLead = 21, maxLead = 90 }: { minLead?: number; maxLead?: number } = {},
): ReturnEntry[] {
  const out: ReturnEntry[] = []
  for (const guest of guests) {
    if (guest.anonymized_at || guest.merged_into) continue
    const stats = statsOf(guest.id)
    if (stats.stays === 0 || !stats.lastArrival) continue
    // Wer schon wieder gebucht hat, braucht keine Einladung — das wäre die Mail,
    // die einen Stammgast fragt, ob er nicht mal wiederkommen will.
    if (stats.upcoming > 0) continue

    const lastArrival = stats.lastArrival
    const anniversary = nextAnniversaryOf(lastArrival, today)
    const inDays = nights(today, anniversary)
    if (inDays < minLead || inDays > maxLead) continue

    out.push({
      guest,
      lastArrival,
      anniversary,
      inDays,
      yearsAgo: Number(anniversary.slice(0, 4)) - Number(lastArrival.slice(0, 4)),
    })
  }
  return out.sort((a, b) => a.inDays - b.inDays)
}

function nextAnniversaryOf(date: ISODate, today: ISODate): ISODate {
  const month = date.slice(5, 7)
  const day = date.slice(8, 10)
  const thisYear = anniversaryIn(Number(today.slice(0, 4)), month, day)
  return thisYear >= today
    ? thisYear
    : anniversaryIn(Number(today.slice(0, 4)) + 1, month, day)
}

/* ────────────────────────────────────────────────────────── VIP */

export type VipSource = 'manual_on' | 'manual_off' | 'revenue' | 'stays' | 'none'

export interface VipVerdict {
  vip: boolean
  /** Woher der Status kommt. Von Hand gesetzt bleibt von berechnet unterscheidbar. */
  source: VipSource
  /** Wäre er es auch ohne Handeingriff? Zeigt die Oberfläche neben dem Schalter. */
  automatic: boolean
}

/**
 * VIP ist eine Entscheidung des Betriebs, keine Eigenschaft des Gastes.
 *
 * Die Schwelle setzt der Vermieter, der Rest ergibt sich. Trotzdem bleibt der
 * Handeingriff: es gibt den Stammgast, der wenig zahlt und trotzdem zählt, und es
 * gibt den Gast, der einmal eine teure Woche gebucht hat und beim nächsten Mal
 * niemand Besonderes ist. Beide Fälle sind häufiger als die Schwelle.
 *
 * `vip_override === false` gewinnt gegen jede Schwelle — sonst wäre „nie VIP" nur
 * eine Bitte und keine Einstellung.
 */
export function vipStatus(stats: GuestStats, policy: GuestPolicy, override?: boolean | null): VipVerdict {
  // `booked` und nicht `revenue`: siehe `GuestPolicy.vipRevenueThreshold`.
  const byRevenue = policy.vipRevenueThreshold != null
    && policy.vipRevenueThreshold > 0
    && stats.booked >= policy.vipRevenueThreshold
  const byStays = policy.vipStayThreshold != null
    && policy.vipStayThreshold > 0
    && stats.stays >= policy.vipStayThreshold
  const automatic = byRevenue || byStays

  if (override === true) return { vip: true, source: 'manual_on', automatic }
  if (override === false) return { vip: false, source: 'manual_off', automatic }
  if (byRevenue) return { vip: true, source: 'revenue', automatic }
  if (byStays) return { vip: true, source: 'stays', automatic }
  return { vip: false, source: 'none', automatic }
}

/* ──────────────────────────────────────────────── Die Kartei als Ganzes */

export interface GuestFileSummary {
  /** Wie viele Datensätze die Leiste beschreibt. */
  guests: number
  /** Davon mit mindestens einem angetretenen Aufenthalt. */
  withStay: number
  /** Mit mindestens ZWEI Aufenthalten — die Zahl, um die es einem Vermieter geht. */
  returning: number
  vip: number
  /** Summe der gebuchten Werte. Siehe `GuestStats.booked`. */
  booked: number
  /** Summe der abgerechneten Umsätze. Siehe `GuestStats.revenue`. */
  invoiced: number
  /** Summe der offenen Beträge aus ausgestellten Rechnungen. */
  open: number
}

/**
 * Die vier Zahlen über der Liste.
 *
 * WARUM ÜBERHAUPT: eine Kartei ohne Kopfzeile ist ein Stapel Karteikarten. Der
 * Vermieter hat beim Öffnen zwei Fragen, und keine davon steht auf einer einzelnen
 * Karte: „wie viele Gäste habe ich" und „wie viele kommen wieder". Die zweite ist
 * die eigentliche Betriebskennzahl — ein Haus mit sechs Einheiten lebt von
 * Wiederkehrern, nicht von Reichweite.
 *
 * WIEDERKEHRER SIND ZWEI AUFENTHALTE, nicht drei und nicht „VIP". Der zweite
 * Besuch ist der, der aus einem Urlaub eine Gewohnheit macht; ab dem dritten
 * bestätigt sich nur noch, was der zweite schon gezeigt hat.
 *
 * GEBUCHT UND ABGERECHNET STEHEN NEBENEINANDER, auch wenn „abgerechnet" in vielen
 * Betrieben dauerhaft null ist. Genau das ist der Punkt: wer beides sieht, versteht
 * in einer Sekunde, dass die Null keine kaputte Kartei ist, sondern eine Aussage
 * über seine eigene Belegführung. Eine einzelne, still gemischte Zahl könnte das
 * nicht erklären.
 *
 * Rechnet ausschliesslich mit dem, was ohnehin schon je Gast gerechnet wurde —
 * keine zusätzliche Abfrage, kein zweiter Durchgang durch die Buchungen.
 */
export function summariseGuestFile(
  guests: GuestProfile[],
  statsOf: (id: string) => GuestStats,
  policy: GuestPolicy,
): GuestFileSummary {
  let withStay = 0
  let returning = 0
  let vip = 0
  // In Cent summiert, weil sich hier hunderte Beträge addieren und ein halber Cent
  // Drift je Gast in der Kopfzeile als glatter Fehler dasteht.
  let bookedCents = 0
  let invoicedCents = 0
  let openCents = 0

  for (const guest of guests) {
    const stats = statsOf(guest.id)
    if (stats.stays > 0) withStay += 1
    if (stats.stays >= 2) returning += 1
    if (vipStatus(stats, policy, guest.vip_override).vip) vip += 1
    bookedCents += toCents(stats.booked)
    invoicedCents += toCents(stats.revenue)
    openCents += toCents(stats.open)
  }

  return {
    guests: guests.length,
    withStay,
    returning,
    vip,
    booked: fromCents(bookedCents),
    invoiced: fromCents(invoicedCents),
    open: fromCents(openCents),
  }
}

/* ────────────────────────────────────────────────────────── Reihenfolge */

/**
 * Wonach die Kartei sortiert werden kann.
 *
 * VIER, NICHT ACHT. Jede weitere Sortierung ist eine Frage, die sich der Vermieter
 * beim Öffnen der Liste stellen müsste — und die drei, die er wirklich hat, sind:
 * „wer war zuletzt da" (der Alltag: jemand ruft an, der gerade abgereist ist),
 * „wer bringt am meisten" (die Preisfrage) und „wer kommt am häufigsten" (die
 * Stammgastfrage). Name A–Z ist die vierte, weil eine Kartei sich alphabetisch
 * durchblättern lassen muss, wenn man nur einen halben Namen im Ohr hat.
 *
 * NICHT DABEI: „ältester Gast", „meiste Nächte", „offener Betrag". Die ersten
 * beiden sind Auswertungen und keine Suche, der dritte ist ein Filter — wer sein
 * Geld sucht, will nicht sortieren, sondern nur die Schuldner sehen.
 */
export type GuestSort = 'recent' | 'value' | 'stays' | 'name'

/**
 * Vergleicht zwei Gäste. Gibt es zur gewählten Kennzahl nichts zu vergleichen,
 * entscheidet der Name — eine Liste, in der gleichwertige Zeilen bei jedem Rendern
 * die Plätze tauschen, ist eine, in der man nichts wiederfindet.
 */
export function compareGuests(
  a: GuestProfile, b: GuestProfile,
  statsOf: (id: string) => GuestStats,
  sort: GuestSort,
): number {
  const byName = () => guestLabel(a).localeCompare(guestLabel(b))
  if (sort === 'name') return byName()

  const sa = statsOf(a.id)
  const sb = statsOf(b.id)

  if (sort === 'value') {
    if (sa.booked !== sb.booked) return sb.booked - sa.booked
    return byName()
  }
  if (sort === 'stays') {
    if (sa.stays !== sb.stays) return sb.stays - sa.stays
    // Bei gleicher Zahl der jüngere Aufenthalt zuerst: von zwei Gästen mit je drei
    // Besuchen ist der interessanter, der letztes Jahr da war und nicht 2019.
    const la = sa.lastStay ?? ''
    const lb = sb.lastStay ?? ''
    if (la !== lb) return lb.localeCompare(la)
    return byName()
  }

  // 'recent': wer gerade da war oder kommt, wird am häufigsten gesucht. Wer nie da
  // war, hat kein Datum und landet deshalb am Ende — nicht am Anfang.
  const la = sa.lastStay ?? ''
  const lb = sb.lastStay ?? ''
  if (la !== lb) return lb.localeCompare(la)
  return byName()
}

/* ────────────────────────────────────────────────────────── Was fehlt */

export type GapCode = 'email' | 'phone' | 'address' | 'country' | 'birthdate' | 'language'
export type GapWeight = 'blocking' | 'useful' | 'nice'

export interface GuestGap {
  code: GapCode
  weight: GapWeight
}

/**
 * Was an dieser Kartei fehlt — nach NUTZEN sortiert, nicht nach Reihenfolge in der
 * Maske.
 *
 * Die Schnellerfassung im Buchungsblatt legt einen Gast mit Namen und vielleicht
 * einer E-Mail-Adresse an. Das ist der Normalfall und kein Fehler; halbfertige
 * Datensätze dürfen deshalb nicht wie Mängel aussehen. Was hilft, ist der Grund:
 * „unvollständig" sagt niemandem etwas, „ohne E-Mail keine Bestätigung möglich"
 * schon — und genau deshalb steht die E-Mail-Adresse oben und das Geburtsdatum
 * unten. Die Texte stehen in der Oberfläche, weil sie zweisprachig sind.
 */
const GAP_ORDER: { code: GapCode; weight: GapWeight; missing: (g: GuestProfile) => boolean }[] = [
  // Ohne sie geht keine Bestätigung, kein Angebot, kein Check-in-Link und keine
  // Rechnung hinaus. Das ist der einzige Mangel, der Arbeit blockiert.
  { code: 'email', weight: 'blocking', missing: (g) => !filled(g.email) },
  // Am Anreisetag der einzige Weg zu jemandem, der vor der Tür steht.
  { code: 'phone', weight: 'useful', missing: (g) => !filled(g.phone) },
  // Pflichtangabe auf der Rechnung, sobald sie keine Kleinbetragsrechnung mehr ist.
  { code: 'address', weight: 'useful', missing: (g) => !filled(g.address) || !filled(g.city) },
  // Herkunftsland: Gästeverzeichnis und Nächtigungsstatistik.
  { code: 'country', weight: 'useful', missing: (g) => !filled(g.country) },
  // Entscheidet über die Ortstaxe-Befreiung bei Kindern.
  { code: 'birthdate', weight: 'nice', missing: (g) => !g.birthdate },
  // Ohne Angabe gehen Belege und Mails auf Deutsch hinaus.
  { code: 'language', weight: 'nice', missing: (g) => !filled(g.language) },
]

export function gapsFor(guest: GuestProfile): GuestGap[] {
  // Ein anonymisierter Datensatz ist nicht unvollständig, sondern absichtlich leer.
  if (guest.anonymized_at) return []
  return GAP_ORDER.filter((g) => g.missing(guest)).map(({ code, weight }) => ({ code, weight }))
}

function filled(v: string | null | undefined): boolean {
  return typeof v === 'string' && v.trim() !== ''
}

/* ────────────────────────────────────────────────────────── Dubletten */

export interface DuplicatePair {
  a: GuestProfile
  b: GuestProfile
  /** 0–100. Ab 60 wird vorgeschlagen, ab 90 ist es praktisch sicher. */
  score: number
  reasons: DuplicateReason[]
}

export type DuplicateReason = 'email' | 'phone' | 'name' | 'name_initial' | 'place'

/**
 * Vergleichsform eines Namens: Kleinbuchstaben, Umlaute aufgelöst, alles außer
 * Buchstaben weg. „Müller-Lüdenscheidt" und „Mueller Ludenscheidt" sollen sich
 * finden, denn genau so schreiben zwei Portale denselben Namen.
 */
export function normName(value: string | null | undefined): string {
  if (!value) return ''
  return value
    .toLowerCase()
    .replace(/ß/g, 'ss')
    .replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue')
    // Restliche Akzente auflösen: „José" und „Jose" sind derselbe Gast.
    .normalize('NFD').replace(/\p{Diacritic}/gu, '')
    .replace(/[^a-z]/g, '')
}

/**
 * E-Mail-Adressen werden nur getrimmt und kleingeschrieben — NICHT normalisiert.
 *
 * Der naheliegende Trick wäre, bei Gmail Punkte und alles nach einem `+` zu
 * entfernen, weil dort `m.huber+urlaub@` und `mhuber@` dasselbe Postfach sind. Das
 * gilt aber nur bei Gmail, und bei anderen Anbietern sind es zwei verschiedene
 * Menschen. Ein falsch zusammengeführter Gast lässt sich nicht rückgängig machen —
 * also lieber eine Dublette übersehen als zwei Personen verschmelzen.
 */
export function normEmail(value: string | null | undefined): string {
  return (value ?? '').trim().toLowerCase()
}

/**
 * Telefonnummern auf die letzten acht Ziffern. Das überbrückt +43/0043/0 und
 * Leerzeichen, ohne bei kurzen Durchwahlen wild zu raten.
 */
export function normPhone(value: string | null | undefined): string {
  const digits = (value ?? '').replace(/\D/g, '')
  return digits.length >= 8 ? digits.slice(-8) : ''
}

/**
 * Findet Gäste, die vermutlich dieselbe Person sind.
 *
 * WARUM ES DAS BRAUCHT: jede Portalbuchung legt einen neuen Gast an, und die
 * Schnellerfassung im Buchungsblatt auch. „Maria Huber" mit zwei Adressen ist nach
 * zwei Saisonen der Normalfall — und die Folge ist, dass die Stammgästin bei ihrem
 * fünften Aufenthalt als Neukundin dasteht.
 *
 * WARUM NUR VORSCHLAGEN: die Zusammenführung ist unumkehrbar. Ein Kandidat, der
 * zwei Brüder mit derselben Adresse zusammenwirft, ist teurer als eine übersehene
 * Dublette. Die Schwellen unten sind deshalb bewusst streng: derselbe Nachname und
 * dieselbe Stadt allein reichen NICHT.
 */
/** Die Angaben, die für einen Dublettenverdacht genügen. */
export interface DublettenKandidat {
  first_name?: string | null
  last_name?: string | null
  email?: string | null
  phone?: string | null
  city?: string | null
  postcode?: string | null
}

/**
 * Wie sehr ähneln sich zwei Gäste?
 *
 * Herausgezogen, damit die nachträgliche Suche (`findDuplicates`) und die
 * Warnung BEI DER ANLAGE (`aehnlicheGaeste`) dieselbe Rechnung benutzen. Zwei
 * Bewertungen, die auseinanderlaufen, wären schlimmer als eine ungenaue: der
 * Vermieter bekäme beim Anlegen keine Warnung und fände denselben Fall später
 * in der Dublettenliste.
 */
export function dublettenWert(
  a: DublettenKandidat, b: DublettenKandidat,
): { score: number; reasons: DuplicateReason[] } {
  const reasons: DuplicateReason[] = []
  let score = 0

  const emailA = normEmail(a.email)
  const emailB = normEmail(b.email)
  if (emailA && emailA === emailB) { score = Math.max(score, 100); reasons.push('email') }

  const phoneA = normPhone(a.phone)
  const phoneB = normPhone(b.phone)
  if (phoneA && phoneA === phoneB) { score = Math.max(score, 85); reasons.push('phone') }

  const lastA = normName(a.last_name)
  const lastB = normName(b.last_name)
  const firstA = normName(a.first_name)
  const firstB = normName(b.first_name)

  if (lastA && lastA === lastB) {
    if (firstA && firstA === firstB) {
      score = Math.max(score, 80)
      reasons.push('name')
    } else if (firstA && firstB && (firstA.startsWith(firstB) || firstB.startsWith(firstA))) {
      // „M." und „Maria" — so trägt ein Portal einen abgekürzten Vornamen ein.
      score = Math.max(score, 70)
      reasons.push('name_initial')
    }
    // Gleicher Ort ODER gleiche PLZ hebt an, begründet aber allein nichts:
    // in einem Dorf heißt die halbe Straße gleich.
    const placeA = normName(a.city) || (a.postcode ?? '').trim()
    const placeB = normName(b.city) || (b.postcode ?? '').trim()
    if (placeA && placeA === placeB && reasons.length > 0) {
      score = Math.min(100, score + 10)
      reasons.push('place')
    }
  }

  return { score, reasons }
}

/**
 * Gibt es diesen Gast schon? — die Prüfung VOR dem Anlegen.
 *
 * `findDuplicates` räumt hinterher auf; das ist die zweitbeste Zeit. Die beste
 * ist, bevor die zweite Zeile entsteht: danach hängen an beiden Buchungen, und
 * das Zusammenführen ist Arbeit, die niemand gerne macht.
 *
 * Bewusst NICHT blockierend gedacht: es gibt echte Fälle gleichen Namens, und
 * ein Vermieter, der seinen Gast kennt, darf nicht ausgesperrt werden. Der
 * Aufrufer zeigt einen Hinweis mit der Möglichkeit, den vorhandenen zu nehmen.
 *
 * @param minScore 70 statt 60 wie in der Liste: hier steht jemand vor einem
 *   Formular und soll nicht bei jedem gleichen Nachnamen aufgehalten werden.
 */
export function aehnlicheGaeste(
  kandidat: DublettenKandidat, guests: GuestProfile[], minScore = 70,
): { guest: GuestProfile; score: number; reasons: DuplicateReason[] }[] {
  return guests
    .filter((g) => !g.anonymized_at && !g.merged_into)
    .map((g) => ({ guest: g, ...dublettenWert(kandidat, g) }))
    .filter((t) => t.score >= minScore)
    .sort((x, y) => y.score - x.score)
    .slice(0, 5)
}

export function findDuplicates(guests: GuestProfile[], minScore = 60): DuplicatePair[] {
  // Anonymisierte und bereits zusammengeführte Zeilen sind keine Kandidaten mehr:
  // dort ist nichts zu retten und nichts zu vereinigen.
  const list = guests.filter((g) => !g.anonymized_at && !g.merged_into)
  const out: DuplicatePair[] = []

  for (let i = 0; i < list.length; i += 1) {
    for (let j = i + 1; j < list.length; j += 1) {
      const a = list[i]!
      const b = list[j]!
      const { score, reasons } = dublettenWert(a, b)
      if (score >= minScore) out.push({ a, b, score, reasons })
    }
  }

  return out.sort((x, y) => y.score - x.score)
}

/* ────────────────────────────────────────────────────────── Löschreife */

export interface RetentionCandidate {
  guest: GuestProfile
  /** Der jüngste Beleg bzw. die letzte Abreise. */
  lastDocument: ISODate
  /** Bis zu diesem Tag ist aufzubewahren; danach darf gelöscht werden. */
  keepUntil: ISODate
}

/**
 * Ende der Aufbewahrungsfrist für einen Beleg.
 *
 * § 132 BAO rechnet ab dem SCHLUSS des Kalenderjahres, nicht ab dem Belegdatum:
 * eine Rechnung vom 15. März 2026 ist bis zum 31. Dezember 2033 aufzubewahren, nicht
 * bis zum 15. März 2033. Wer taggenau rechnet, löscht neun Monate zu früh.
 */
export function keepUntil(document: ISODate, years: number): ISODate {
  const year = Number(document.slice(0, 4)) + years
  return `${year}-12-31`
}

/**
 * Gäste, deren Daten nicht mehr gebraucht werden.
 *
 * WIRD NUR VORGESCHLAGEN, NIE AUSGEFÜHRT. Ein Hintergrundjob, der eigenmächtig
 * Kundendaten löscht, ist ein Fehler, den man genau einmal macht: der Vermieter
 * merkt es erst, wenn ein Stammgast anruft und niemand weiß mehr, wer er ist.
 * Art. 5 Abs. 1 lit. e verlangt, dass Daten nicht länger als nötig gespeichert
 * werden — nicht, dass eine Maschine das allein entscheidet.
 */
export function retentionCandidates(
  guests: GuestProfile[],
  statsOf: (id: string) => GuestStats,
  policy: GuestPolicy,
  today: ISODate,
): RetentionCandidate[] {
  const out: RetentionCandidate[] = []
  for (const guest of guests) {
    if (guest.anonymized_at || guest.merged_into) continue
    const stats = statsOf(guest.id)
    // Ein offener Vorgang hält alles auf: laufender Aufenthalt, unbezahlte Rechnung.
    if (stats.upcoming > 0 || stats.open > 0) continue
    if (!stats.lastDocument) continue
    const until = keepUntil(stats.lastDocument, policy.retentionYears)
    if (until >= today) continue
    out.push({ guest, lastDocument: stats.lastDocument, keepUntil: until })
  }
  return out.sort((a, b) => a.lastDocument.localeCompare(b.lastDocument))
}

/* ────────────────────────────────────────────────────────── Auskunft */

export interface GuestExport {
  /** Was diese Datei ist, für den Gast lesbar. */
  auskunft: {
    erstellt_am: string
    rechtsgrundlage: string
    betrieb: string
    hinweis: string
  }
  stammdaten: Record<string, unknown>
  /**
   * Gesondert ausgewiesen, weil es eine besondere Kategorie nach Art. 9 DSGVO ist.
   * Der Gast soll auf einen Blick sehen, dass über ihn Gesundheitsangaben
   * gespeichert sind, und auf welcher Grundlage — nicht zwischen Kissenwunsch und
   * Kennzeichen versteckt.
   */
  besondere_kategorien_art9: {
    vorhanden: boolean
    rechtsgrundlage: string | null
    erfasst_am: string | null
    allergien: string | null
    ernaehrung: string | null
  }
  einwilligungen: Record<string, unknown>
  kennzahlen: GuestStats
  aufenthalte: Record<string, unknown>[]
  rechnungen: Record<string, unknown>[]
  zahlungen: Record<string, unknown>[]
  /** Vom Server nachgereicht (Meldescheine, Protokoll) — siehe guest_data_export(). */
  meldescheine?: unknown[]
  loeschprotokoll?: unknown[]
}

export interface ExportInput extends StatsInput {
  guest: GuestProfile
  tenantName: string
  unitName: (unitId: string) => string
}

/**
 * Auskunft nach Art. 15 und Datenübertragbarkeit nach Art. 20 in einer Datei.
 *
 * Der Vermieter hat einen Monat Zeit zu antworten (Art. 12 Abs. 3). Ohne Knopf heißt
 * das in der Praxis: jemand exportiert die Datenbank von Hand, oder es passiert
 * nicht. Deshalb deutsche Schlüssel statt Spaltennamen — der Empfänger ist der Gast
 * und nicht ein Entwickler, und ein `pref_bedding: null` beantwortet keine Anfrage.
 *
 * Die Meldescheinzeilen und das Löschprotokoll kommen aus `guest_data_export()`
 * dazu; im Demomodus gibt es sie nicht.
 */
export function buildGuestExport(input: ExportInput): GuestExport {
  const { guest } = input
  const stats = guestStats(input)
  const bookings = input.bookings
    .filter((b) => b.guest_id === guest.id && b.kind !== 'block')
    .sort((a, b) => a.arrival.localeCompare(b.arrival))
  const bookingIds = new Set(bookings.map((b) => b.id))
  const invoices = input.invoices.filter(
    (i) => i.guest_id === guest.id || (i.booking_id !== null && bookingIds.has(i.booking_id)))
  const invoiceIds = new Set(invoices.map((i) => i.id))
  const payments = input.payments.filter(
    (p) => (p.booking_id !== null && bookingIds.has(p.booking_id))
      || (p.invoice_id !== null && invoiceIds.has(p.invoice_id)))

  const hasSpecial = filled(guest.allergies) || filled(guest.dietary)

  return {
    auskunft: {
      erstellt_am: new Date().toISOString(),
      rechtsgrundlage: 'Auskunft nach Art. 15 DSGVO, Datenübertragbarkeit nach Art. 20 DSGVO',
      betrieb: input.tenantName,
      hinweis: 'Diese Datei enthält alle zu Ihnen gespeicherten Daten, einschließlich '
        + 'der aus Ihren Buchungen und Belegen errechneten Kennzahlen. Ausgestellte '
        + 'Rechnungen unterliegen der gesetzlichen Aufbewahrungspflicht und können '
        + 'nicht gelöscht werden.',
    },
    stammdaten: {
      anrede: guest.salutation ?? null,
      titel: guest.title ?? null,
      vorname: guest.first_name ?? null,
      nachname: guest.last_name ?? null,
      firma: guest.company ?? null,
      uid: guest.vat_id ?? null,
      email: guest.email ?? null,
      telefon: guest.phone ?? null,
      anschrift: guest.address ?? null,
      plz: guest.postcode ?? null,
      ort: guest.city ?? null,
      land: guest.country ?? null,
      staatsangehoerigkeit: guest.nationality ?? null,
      sprache: guest.language ?? null,
      geburtsdatum: guest.birthdate ?? null,
      ausweisart: guest.id_document_type ?? null,
      ausweisnummer: guest.id_document ?? null,
      vorlieben: {
        etage: guest.pref_floor ?? null,
        rauchen: guest.pref_smoking ?? null,
        zimmerlage: guest.pref_location ?? null,
        bett_und_kissen: guest.pref_bedding ?? null,
        ankunftszeit: guest.pref_arrival_time ?? null,
        sonstiges: guest.preferences_note ?? null,
      },
      anlass: guest.occasion ?? null,
      anlass_datum: guest.occasion_date ?? null,
      kennzeichen: guest.vehicle_plate ?? null,
      fahrzeug: guest.vehicle_note ?? null,
      haustier: guest.pets ?? null,
      herkunft: guest.source ?? null,
      schlagworte: guest.tags ?? [],
      interne_notiz: guest.notes ?? null,
      // Auch das ist Auskunft: dass ein Sperrvermerk besteht, muss der Gast
      // erfahren — es ist eine Bewertung seiner Person.
      sperrvermerk: Boolean(guest.blacklisted),
      anonymisiert_am: guest.anonymized_at ?? null,
    },
    besondere_kategorien_art9: {
      vorhanden: hasSpecial,
      rechtsgrundlage: hasSpecial ? (guest.health_basis ?? null) : null,
      erfasst_am: hasSpecial ? (guest.health_recorded_at ?? null) : null,
      allergien: guest.allergies ?? null,
      ernaehrung: guest.dietary ?? null,
    },
    einwilligungen: {
      werbung: Boolean(guest.marketing_consent),
      werbung_erteilt_am: guest.marketing_consent_at ?? null,
      werbung_herkunft: guest.marketing_consent_source ?? null,
    },
    kennzahlen: stats,
    aufenthalte: bookings.map((b) => ({
      referenz: b.reference,
      einheit: input.unitName(b.unit_id),
      anreise: b.arrival,
      abreise: b.departure,
      naechte: nights(b.arrival, b.departure),
      erwachsene: b.adults,
      kinder: b.children,
      status: b.status,
      kanal: b.channel,
      betrag_brutto: b.total_gross,
      waehrung: b.currency,
      hinweis_an_gast: b.notes ?? null,
      storniert_am: b.cancelled_at ?? null,
    })),
    rechnungen: invoices
      .filter((i) => Boolean(i.issued_at))
      .map((i) => ({
        nummer: i.number,
        art: i.type,
        datum: i.issue_date,
        betrag_brutto: i.total_gross,
        bezahlt: i.paid_amount,
        waehrung: i.currency,
        status: i.status,
      })),
    zahlungen: payments.map((p) => ({
      datum: p.received_on,
      betrag: p.amount,
      waehrung: p.currency,
      art: p.kind,
      weg: p.method,
      referenz: p.reference ?? null,
    })),
  }
}

/** Anzeigename, der auch bei einem halbfertigen Datensatz etwas hergibt. */
export function guestLabel(guest: GuestProfile, fallback = 'Ohne Namen'): string {
  const name = [guest.first_name, guest.last_name].filter(filled).join(' ').trim()
  return name || guest.company?.trim() || guest.email?.trim() || fallback
}
